import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Star, Users, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { MOCK_CATEGORIES, MOCK_COURSES } from '../utils/mockData';

function CoursesPage() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: '',
    maxPrice: '',
    minRating: '',
  });

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  // Keep local search state in sync with URL query (?q=...)
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    setSearchQuery(urlQuery);
    // eslint-disable-next-line
  }, [searchParams]);

  // Recompute courses when filters or search text change
  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line
  }, [filters, searchQuery]);

  const fetchCategories = async () => {
    // Simulate loading and set mock categories
    setTimeout(() => {
      setCategories(MOCK_CATEGORIES);
    }, 200);
  };

  const fetchCourses = async () => {
    setLoading(true);

    // Simulate loading delay
    setTimeout(() => {
      let filtered = [...MOCK_COURSES];

      // Apply filters
      if (filters.category) {
        filtered = filtered.filter(c => c.category_id == filters.category);
      }
      if (searchQuery) {
        filtered = filtered.filter(c =>
          c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      if (filters.minPrice) {
        filtered = filtered.filter(c => c.price >= parseFloat(filters.minPrice));
      }
      if (filters.maxPrice) {
        filtered = filtered.filter(c => c.price <= parseFloat(filters.maxPrice));
      }
      if (filters.minRating) {
        filtered = filtered.filter(c => c.rating >= parseFloat(filters.minRating));
      }

      setCourses(filtered);
      setLoading(false);
    }, 400);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="courses-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">Explore Courses</h1>
          <p className="text-lg text-slate-600">Discover your next learning adventure</p>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-700" />
                <h3 className="text-lg font-bold text-slate-900">Filters</h3>
              </div>
              {(filters.category || filters.minRating || filters.minPrice || filters.maxPrice) && (
                <button
                  onClick={() => setFilters({ category: '', minPrice: '', maxPrice: '', minRating: '' })}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                >
                  Clear All
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Category
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="input-field w-full"
                  data-testid="category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rating Filter */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span>Minimum Rating</span>
                  </div>
                </label>
                <select
                  value={filters.minRating}
                  onChange={(e) => handleFilterChange('minRating', e.target.value)}
                  className="input-field w-full"
                  data-testid="rating-filter"
                >
                  <option value="">All Ratings</option>
                  <option value="4.5">4.5+ Stars</option>
                  <option value="4">4+ Stars</option>
                  <option value="3">3+ Stars</option>
                </select>
              </div>

              {/* Min Price Filter */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Min Price ($)
                </label>
                <input
                  type="number"
                  placeholder="0"
                  min="0"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="input-field w-full"
                  data-testid="min-price-filter"
                />
              </div>

              {/* Max Price Filter */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Max Price ($)
                </label>
                <input
                  type="number"
                  placeholder="100"
                  min="0"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="input-field w-full"
                  data-testid="max-price-filter"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-slate-600">
            Showing <span className="font-semibold text-slate-900">{courses.length}</span> {courses.length === 1 ? 'course' : 'courses'}
          </p>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              Loading...
            </div>
          )}
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shimmer h-96" />
            ))}
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-600 text-lg">No courses found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CourseCard({ course, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link to={`/courses/${course.id}`} data-testid={`course-card-${course.id}`}>
        <div className="course-card h-full flex flex-col">
          <div className="relative overflow-hidden">
            <img
              src={course.thumbnail}
              alt={course.title}
              className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {course.rating > 0 && (
              <div className="absolute top-4 right-4 bg-white rounded-full px-3 py-1 flex items-center gap-1 shadow-lg">
                <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                <span className="font-semibold text-sm">{course.rating}</span>
              </div>
            )}
          </div>
          <div className="p-6 flex-grow flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <span className="badge badge-blue">{course.level}</span>
              {course.students_count > 50 && <span className="badge badge-amber">Best Seller</span>}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2">{course.title}</h3>
            <p className="text-slate-600 text-sm mb-4 line-clamp-2">{course.description}</p>
            <div className="flex items-center gap-3 text-sm text-slate-600 mb-4">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{course.students_count}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{course.lessons?.length || 0} lessons</span>
              </div>
            </div>
            <div className="mt-auto flex items-center justify-between">
              <div className="text-2xl font-extrabold text-blue-600">${course.price}</div>
              <button className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1">
                Learn More <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default CoursesPage;
